import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, Camera, Signature, MapPin, Save, 
  Trash2, CheckCircle, AlertTriangle, Loader2,
  Package, FileText, Download, Paperclip, Mail, Send
} from "lucide-react";

const LOGO_URL = "/nassau-logo.png";

export default function DeliveryCapture() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Dynamic terminology based on job type
  const isCollection = job?.job_type === "collection";
  const jobTypeLabel = isCollection ? "Collection" : "Delivery";
  const addressLabel = isCollection ? "Collection Address" : "Delivery Address";
  const itemsLabel = isCollection ? "Items Being Collected" : "Items Being Delivered";
  const photoLabel = isCollection ? "Collection Photo" : "Delivery Photo";
  const recipientLabel = isCollection ? "Who handed over the items?" : "Who received the delivery?";
  
  const [signatureData, setSignatureData] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  const [recipientName, setRecipientName] = useState("");
  const [driverNotes, setDriverNotes] = useState("");
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [locationStatus, setLocationStatus] = useState("pending");
  
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const [itemsDescription, setItemsDescription] = useState("");
  const [savingItems, setSavingItems] = useState(false);

  useEffect(() => {
    fetchJob();
    getLocation();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const res = await api.get(`/jobs/${jobId}`);
      setJob(res.data);
      setItemsDescription(res.data.items_description || "");
    } catch (error) {
      toast.error("Failed to load job details");
      navigate("/driver");
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus("unavailable");
      return;
    }
    
    setLocationStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setLocationStatus("success");
      },
      (error) => {
        console.error("Geolocation error:", error);
        setLocationStatus("error");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Signature Pad Drawing
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#003366";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  useEffect(() => {
    initCanvas();
  }, [initCanvas]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const coords = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const coords = getCoordinates(e);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (hasSignature) {
      const canvas = canvasRef.current;
      setSignatureData(canvas.toDataURL("image/png"));
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
    setHasSignature(false);
  };

  // Photo Capture
  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setPhotoData(event.target.result);
      toast.success("Photo captured!");
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhotoData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Submit Proof
  const handleSubmit = async () => {
    if (!signatureData) {
      toast.error("Please capture customer signature");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const proofData = {
        job_id: jobId,
        signature_data: signatureData,
        photo_data: photoData,
        latitude: location.latitude,
        longitude: location.longitude,
        driver_notes: driverNotes || null,
        recipient_name: recipientName || null
      };
      
      const response = await api.post("/proofs", proofData);
      toast.success(`${jobTypeLabel} completed successfully!`);
      navigate(`/driver/docket/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit proof");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!recipientEmail) {
      toast.error("Please enter an email address");
      return;
    }
    
    setSendingEmail(true);
    try {
      await api.post(`/jobs/${jobId}/email`, {
        job_id: jobId,
        recipient_email: recipientEmail
      });
      toast.success(`${jobTypeLabel} docket sent successfully!`);
      setEmailDialogOpen(false);
      setRecipientEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSaveItems = async () => {
    setSavingItems(true);
    try {
      await api.put(`/jobs/${jobId}`, {
        items_description: itemsDescription
      });
      setJob({ ...job, items_description: itemsDescription });
      toast.success("Items description saved");
    } catch (error) {
      toast.error("Failed to save items description");
    } finally {
      setSavingItems(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="delivery-capture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 border-b border-slate-200">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/driver")}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <img src={LOGO_URL} alt="Nassau Engineering" className="h-8" />
          <span className="font-semibold text-slate-800">Capture {jobTypeLabel}</span>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Job Info */}
        <Card className="border-l-4 border-l-[#003366]">
          <CardContent className="p-4">
            <p className="font-mono text-sm text-[#003366] font-medium">{job?.job_number}</p>
            <h2 className="text-lg font-semibold text-slate-900 mt-1">{job?.customer_name}</h2>
            <p className="text-slate-600 flex items-start gap-2 mt-2">
              <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
              {job?.delivery_address}
            </p>
            {job?.time_window && (
              <p className="text-slate-500 text-sm mt-1">Time: {job.time_window}</p>
            )}
            
            {/* Email Docket Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEmailDialogOpen(true)}
              className="mt-3 w-full"
              data-testid="email-docket-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email {jobTypeLabel} Docket
            </Button>
          </CardContent>
        </Card>

        {/* Items Being Delivered/Collected - Editable */}
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <Package className="h-5 w-5" />
              {itemsLabel}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <Textarea
              value={itemsDescription}
              onChange={(e) => setItemsDescription(e.target.value)}
              placeholder={`Enter details of items being ${isCollection ? 'collected' : 'delivered'}...

Example:
- 2x Steel Beams (10ft)
- 1x Control Panel Unit
- 5x Pipe Fittings

Add any notes about condition, quantities, serial numbers etc.`}
              className="min-h-[350px] text-base leading-loose resize-y font-normal bg-white p-4 whitespace-pre-wrap"
              data-testid="items-description-input"
            />
            <div className="flex justify-end">
              <Button
                onClick={handleSaveItems}
                disabled={savingItems || itemsDescription === (job?.items_description || "")}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="save-items-btn"
              >
                {savingItems ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="h-4 w-4" />
                    Save Items
                  </span>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Special Instructions */}
        {job?.special_instructions && (
          <Card className="border-l-4 border-l-blue-500 bg-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-blue-800">
                <AlertTriangle className="h-5 w-5" />
                Special Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-blue-900">{job.special_instructions}</p>
            </CardContent>
          </Card>
        )}

        {/* Attached Documents */}
        {job?.documents && job.documents.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Paperclip className="h-5 w-5 text-[#003366]" />
                Attached Documents ({job.documents.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {job.documents.map((doc) => (
                <div 
                  key={doc.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">{doc.filename}</p>
                      <p className="text-xs text-slate-500">{(doc.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await api.get(`/jobs/${job.id}/documents/${doc.id}`, {
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', doc.filename);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                      } catch (error) {
                        toast.error("Failed to download document");
                      }
                    }}
                    data-testid={`download-doc-${doc.id}`}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    View
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Location Status */}
        <Card className={`${
          locationStatus === "success" ? "bg-emerald-50 border-emerald-200" :
          locationStatus === "error" ? "bg-amber-50 border-amber-200" :
          "bg-slate-50"
        }`}>
          <CardContent className="p-4 flex items-center gap-3">
            <MapPin className={`h-5 w-5 ${
              locationStatus === "success" ? "text-emerald-600" :
              locationStatus === "error" ? "text-amber-600" :
              "text-slate-400"
            }`} />
            <div className="flex-1">
              {locationStatus === "loading" && <span className="text-slate-600">Getting location...</span>}
              {locationStatus === "success" && (
                <span className="text-emerald-700 font-medium">
                  Location captured: {location.latitude?.toFixed(6)}, {location.longitude?.toFixed(6)}
                </span>
              )}
              {locationStatus === "error" && (
                <span className="text-amber-700">Location unavailable - {jobTypeLabel.toLowerCase()} will proceed</span>
              )}
              {locationStatus === "unavailable" && (
                <span className="text-slate-600">Geolocation not supported</span>
              )}
            </div>
            {locationStatus === "error" && (
              <Button variant="outline" size="sm" onClick={getLocation}>Retry</Button>
            )}
          </CardContent>
        </Card>

        {/* Signature Pad */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Signature className="h-5 w-5 text-[#003366]" />
              Customer Signature <span className="text-[#D32F2F]">*</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="recipient">Recipient Name</Label>
              <Input
                id="recipient"
                data-testid="recipient-name"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder={recipientLabel}
                className="h-12"
              />
            </div>
            
            <div 
              className="border-2 border-dashed border-slate-300 bg-slate-50 rounded-lg overflow-hidden"
              data-testid="signature-pad-container"
            >
              <canvas
                ref={canvasRef}
                width={350}
                height={200}
                className="signature-canvas w-full bg-white"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                data-testid="signature-canvas"
              />
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`text-sm ${hasSignature ? "text-emerald-600" : "text-slate-500"}`}>
                {hasSignature ? "✓ Signature captured" : "Sign above"}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={clearSignature}
                disabled={!hasSignature}
                data-testid="clear-signature"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Photo Capture */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="h-5 w-5 text-[#003366]" />
              {photoLabel}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!photoData ? (
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <Camera className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                <p className="text-slate-600 mb-4">Take a photo of the {isCollection ? 'collected' : 'delivered'} goods</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoCapture}
                  className="hidden"
                  id="photo-input"
                  data-testid="photo-input"
                />
                <label htmlFor="photo-input">
                  <Button asChild className="bg-[#003366] hover:bg-[#002244]">
                    <span>
                      <Camera className="h-4 w-4 mr-2" />
                      Capture Photo
                    </span>
                  </Button>
                </label>
              </div>
            ) : (
              <div className="space-y-3">
                <img
                  src={photoData}
                  alt={jobTypeLabel}
                  className="w-full rounded-lg border border-slate-200"
                  data-testid="captured-photo"
                />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-emerald-600">✓ Photo captured</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={removePhoto}
                    data-testid="remove-photo"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Driver Notes */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Driver Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={driverNotes}
              onChange={(e) => setDriverNotes(e.target.value)}
              placeholder="Special instructions, access codes, issues encountered..."
              className="min-h-[100px]"
              data-testid="driver-notes"
            />
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={!signatureData || submitting}
          className="w-full h-14 text-lg bg-[#D32F2F] hover:bg-[#B71C1C]"
          data-testid="submit-delivery"
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Completing {jobTypeLabel}...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Complete {jobTypeLabel}
            </span>
          )}
        </Button>
      </main>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#003366]" />
              Email {jobTypeLabel} Docket
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Send the {jobTypeLabel.toLowerCase()} docket for <span className="font-mono font-medium">{job?.job_number}</span> to an email address. 
              This will include all job details{job?.documents?.length > 0 ? ` and ${job.documents.length} attached document(s)` : ""}.
            </p>
            <div className="space-y-2">
              <Label htmlFor="email">Recipient Email Address</Label>
              <Input
                id="email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="customer@example.com"
                data-testid="email-recipient-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || !recipientEmail}
              className="bg-[#003366] hover:bg-[#002244]"
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Send Email
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
